import { Clock } from "lucide-react";
import React, { useEffect, useState } from "react";
import { useTheme } from "../contexts/ThemeContext";

interface TimeDisplay {
  label: string;
  time: string;
  date: string;
  zone: string;
  tzId: string;
}

const WorldClock: React.FC = () => {
  const { theme } = useTheme();
  const isDarkMode = theme === "dark";
  const [times, setTimes] = useState<TimeDisplay[]>([]);
  const [userTimeZone, setUserTimeZone] = useState<string>("");

  useEffect(() => {
    try {
      setUserTimeZone(Intl.DateTimeFormat().resolvedOptions().timeZone);
    } catch (e) {
      console.warn("Could not detect user timezone", e);
    }
  }, []);

  useEffect(() => {
    const updateTimes = () => {
      const now = new Date();

      const formatTime = (timeZone: string) => {
        try {
          return new Intl.DateTimeFormat("en-US", {
            timeZone,
            hour: "numeric",
            minute: "2-digit",
            second: "2-digit",
            hour12: true,
          }).format(now);
        } catch (e) {
          return "--:--:--";
        }
      };

      const formatDate = (timeZone: string) => {
        try {
          return new Intl.DateTimeFormat("en-US", {
            timeZone,
            weekday: "short",
            month: "short",
            day: "numeric",
          }).format(now);
        } catch (e) {
          return "---, -- --";
        }
      };

      const zones = [
        { label: "UTC", tzId: "UTC", name: "Universal Coordinated Time" },
        { label: "PST", tzId: "America/Los_Angeles", name: "Pacific Standard Time" },
        { label: "IST", tzId: "Asia/Kolkata", name: "Indian Standard Time" },
      ];

      setTimes(zones.map(z => ({
        label: z.label,
        time: formatTime(z.tzId),
        date: formatDate(z.tzId),
        zone: z.name,
        tzId: z.tzId
      })));
    };

    updateTimes();
    const timer = setInterval(updateTimes, 1000);

    return () => clearInterval(timer);
  }, []);

  const isCurrentZone = (tzId: string) => {
     if (!userTimeZone) return false;
     return tzId === userTimeZone;
  };

  return (

    <>
      {/* Mobile View: Compact Single Row */}
      <div className={`md:hidden w-full rounded-lg shadow p-3 mb-6 overflow-x-auto ${isDarkMode ? "bg-rp-surface" : "bg-white"}`}>
        <div className="flex items-center justify-between min-w-max gap-6 px-2">
          {times.map((t) => {
            const isHighlight = isCurrentZone(t.tzId);
            return (
              <div key={t.label} className="flex flex-col items-center">
                 <div className="flex items-center space-x-1.5 mb-0.5">
                   <Clock className={`w-3 h-3 ${isHighlight ? (isDarkMode ? "text-rp-amber" : "text-blue-500") : (isDarkMode ? "text-rp-grey/40" : "text-gray-400")}`} />
                   <span className={`text-xs font-bold ${
                     isHighlight 
                       ? isDarkMode ? "text-rp-amber" : "text-blue-600"
                       : isDarkMode ? "text-rp-grey/70" : "text-gray-600"
                   }`}>
                     {t.label}
                   </span>
                 </div>
                 <div className={`text-sm font-mono font-bold leading-none ${
                     isHighlight 
                       ? isDarkMode ? "text-rp-amber" : "text-blue-600"
                       : isDarkMode ? "text-rp-grey" : "text-gray-900"
                 }`}>
                   {t.time}
                 </div>
                 <div className={`text-[10px] ${isDarkMode ? "text-rp-grey/40" : "text-gray-400"}`}>
                   {t.date}
                 </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Desktop View: Full Cards */}
      <div className="hidden md:grid md:grid-cols-3 gap-4 mb-6">
        {times.map((t) => {
          const isHighlight = isCurrentZone(t.tzId);
          
          return (
            <div
              key={t.label}
              className={`${
                isDarkMode ? "bg-rp-surface" : "bg-white"
              } rounded-lg shadow p-4 flex flex-col sm:flex-row items-start sm:items-center justify-between border-l-4 transition-all duration-200 ${
                isHighlight 
                  ? isDarkMode ? "border-rp-amber ring-1 ring-rp-amber/20" : "border-blue-500 ring-1 ring-blue-500/20"
                  : isDarkMode ? "border-rp-separator" : "border-gray-200"
              }`}
            >
              <div className="flex items-center mb-2 sm:mb-0 w-full sm:w-auto overflow-hidden">
                <div className={`p-2 rounded-lg mr-3 shrink-0 ${
                  isHighlight
                    ? isDarkMode ? "bg-rp-amber/10 text-rp-amber" : "bg-blue-50 text-blue-600"
                    : isDarkMode ? "bg-rp-surface-raised text-rp-grey/40" : "bg-gray-100 text-gray-500"
                }`}>
                  <Clock className="w-5 h-5" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className={`text-sm font-medium ${isDarkMode ? "text-rp-grey/50" : "text-gray-500"}`}>
                    {t.label}
                  </p>
                  <p className={`text-xs truncate ${isDarkMode ? "text-rp-grey/40" : "text-gray-400"}`} title={t.zone}>
                    {t.zone}
                  </p>
                </div>
              </div>
              
              <div className={`text-left sm:text-right w-full sm:w-auto ${
                 isHighlight
                 ? isDarkMode ? "text-rp-amber" : "text-blue-600"
                 : isDarkMode ? "text-rp-grey" : "text-gray-900"
              }`}>
                <div className="text-xl font-bold font-mono leading-none mb-1">
                  {t.time}
                </div>
                <div className={`text-xs ${isDarkMode ? "text-rp-grey/40" : "text-gray-400"}`}>
                  {t.date}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </>
  );
};

export default WorldClock;
